/*
================================================================================
File: src/pages/Ecommerce.js
Description: Displays a store for purchasing credits and services.
================================================================================
*/
import React from 'react';
import PageContent from '../components/common/PageContent';

const ProductCard = ({ title, description, cost, link, pricePerCredit, bestValue }) => (
  <div className="relative bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow flex flex-col h-full">
    {bestValue && (
      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
        <span className="bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md">
          Best Value
        </span>
      </div>
    )}

    <div className="p-5 flex-grow">
      <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-lg">{title}</h3>
      {description && (
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 leading-relaxed">
          {description}
        </p>
      )}
      {pricePerCredit && (
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-3 font-medium">
          {pricePerCredit} per credit
        </p>
      )}
    </div>

    <div className="p-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
      <span className="text-2xl font-bold text-orange-600 dark:text-orange-400">{cost}</span>
      <button
        onClick={() => window.open(link, "_blank", "noopener,noreferrer")}
        className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded-lg transition-colors"
      >
        Purchase
      </button>
    </div>
  </div>
);

const Ecommerce = () => {
  const categories = [
    {
      label: "Business Services",
      icon: (
        <svg
          className="w-6 h-6 text-orange-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
          />
        </svg>
      ),
      products: [
        {
          title: "Business Logo",
          description: "We create your first set for logos (one for each brand) with your business launcher.",
          cost: "$20.00",
          link: "https://loveamerican.infusionsoft.app/app/orderForms/One-Business-Launcher",
        },
        {
          title: "Business Launcher",
          description: "This is for a partner or family member that wants their own websites, etc.",
          cost: "$1,500.00",
          link: "https://loveamerican.infusionsoft.app/app/orderForms/One-Business-Launcher",
        },
      ],
    },
    {
      label: "AI-Query Credits",
      icon: (
        <svg
          className="w-6 h-6 text-indigo-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
          />
        </svg>
      ),
      products: [
        {
          title: "10 Auto-Query Credits",
          cost: "$9.99",
          pricePerCredit: "$1.00",
          link: "https://loveamerican.infusionsoft.app/app/orderForms/Big-Dog-Query-Credits-10-Credits",
        },
        {
          title: "20 Auto-Query Credits",
          cost: "$17.99",
          pricePerCredit: "$0.90",
          link: "https://loveamerican.infusionsoft.app/app/orderForms/Big-Dog-Query-Credits-20-Credits",
        },
        {
          title: "50 Auto-Query Credits",
          cost: "$39.99",
          pricePerCredit: "$0.80",
          link: "https://loveamerican.infusionsoft.app/app/orderForms/Big-Dog-Query-Credits-50-Credits",
        },
        {
          title: "100 Auto-Query Credits",
          cost: "$69.99",
          pricePerCredit: "$0.70",
          bestValue: true,
          link: "https://loveamerican.infusionsoft.app/app/orderForms/Big-Dog-Query-Credits-100-Credits",
        },
      ],
    },
    {
      label: "Skip-Trace Credits",
      icon: (
        <svg
          className="w-6 h-6 text-green-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
      ),
      products: [
        {
          title: "10 Skip-Trace Credits",
          cost: "$9.99",
          pricePerCredit: "$1.00",
          link: "https://loveamerican.infusionsoft.app/app/orderForms/Big-Dog-Skip-Trace-Credits-10-Credits",
        },
        {
          title: "20 Skip-Trace Credits",
          cost: "$17.99",
          pricePerCredit: "$0.90",
          link: "https://loveamerican.infusionsoft.app/app/orderForms/Big-Dog-Skip-Trace-Credits-20-Credits",
        },
        {
          title: "50 Skip-Trace Credits",
          cost: "$39.99",
          pricePerCredit: "$0.80",
          link: "https://loveamerican.infusionsoft.app/app/orderForms/Big-Dog-Skip-Trace-Credits-50-Credits",
        },
        {
          title: "100 Skip-Trace Credits",
          cost: "$69.99",
          pricePerCredit: "$0.70",
          bestValue: true,
          link: "https://loveamerican.infusionsoft.app/app/orderForms/Big-Dog-Skip-Trace-Credits-100-Credits?",
        },
      ],
    },
  ];

  return (
    <PageContent title="Credits Store" error={null}>
      <div className="p-8 space-y-10">
        {/* Notice Banner */}
        <div className="flex gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg text-amber-800 dark:text-amber-300">
          <svg
            className="w-5 h-5 flex-shrink-0 mt-0.5"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 5v8a2 2 0 01-2 2h-5l-5 4v-4H4a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2zm-11-1a1 1 0 11-2 0 1 1 0 012 0zM8 8a1 1 0 000 2h6a1 1 0 000-2H8zm0 3a1 1 0 000 2h3a1 1 0 000-2H8z"
              clipRule="evenodd"
            />
          </svg>
          <div>
            <p className="text-sm font-medium">
              After making a purchase, please send an email to{" "}
              <span className="font-semibold">support@loveamericanhomes.com</span> with your payment
              receipt so we can process your request or add the credits to your account.
            </p>
          </div>
        </div>

        {/* Category Sections */}
        {categories.map((category) => (
          <section key={category.label}>
            <div className="flex items-center gap-3 mb-6">
              {category.icon}
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
                {category.label}
              </h2>
            </div>
            <div
              className={`grid gap-5 ${
                category.label === "Business Services"
                  ? "grid-cols-1 md:grid-cols-2"
                  : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"
              }`}
            >
              {category.products.map((product, idx) => (
                <ProductCard
                  key={idx}
                  title={product.title}
                  description={product.description}
                  cost={product.cost}
                  link={product.link}
                  pricePerCredit={product.pricePerCredit}
                  bestValue={product.bestValue}
                />
              ))}
            </div>
          </section>
        ))}
      </div>
    </PageContent>
  );
};

export default Ecommerce;